import React, { useState, createContext, useEffect } from "react";
import { toast } from "react-toastify";
import { lookUpColors, lookUpEngraveFontOptions, lookUpKeyOptions, lookUpLockMaterialDependencies, lookUpLockMaterials, lookUpShackleTypes, lookUpKeyCodeBucketTypes, lookUpSalesAreas } from "../providers/lookUpApi";
import { useOktaAuth } from "@okta/okta-react";
import Color from "../models/LookUp/Color";
import KeyOption from "../models/LookUp/KeyOption";
import LockMaterial from "../models/LookUp/LockMaterial";
import ShackleType from "../models/LookUp/ShackleType";
import EngraveFontOption from "../models/LookUp/EngraveFontOption";
import LockMaterialDependency from "../models/LookUp/LockMaterialDependency";
import { LookupContextType } from "../types/lookupContextType";
import KeyCodeBucketType from "../models/LookUp/KeyCodeBucketType";
import SalesArea from "../models/LookUp/SalesArea";
import { useLogger } from "../hooks/useLogger";

export const LookupContext = createContext<LookupContextType>({} as LookupContextType)

interface ILookupContextProviderProps extends React.PropsWithChildren {
}

export const LookupContextProvider = (props: ILookupContextProviderProps) => {
  const { authState } = useOktaAuth();
  const logger = useLogger();

  const [lockColorOptions, setLockColorOptions] = useState<Color[]>([])
  const [keyOptions, setKeyOptions] = useState<KeyOption[]>([])
  const [lockMaterials, setLockMaterials] = useState<LockMaterial[]>([])
  const [shackleTypes, setShackleTypes] = useState<ShackleType[]>([])
  const [engravingFontOptions, setEngravingFontOptions] = useState<EngraveFontOption[]>([])
  const [lockMaterialDependencies, setLockMaterialDependencies] = useState<LockMaterialDependency[]>([])
  const [keyCodeBucketTypes, setKeyCodeBucketTypes] = useState<KeyCodeBucketType[]>([])
  const [salesAreas, setSalesAreas] = useState<SalesArea[]>([])

  const handleLookUpFetch = async (authToken: string, isAuthenticated: boolean) => {
    try {
      const colorsPromise = lookUpColors(authToken, isAuthenticated)
      const keyOptionsPromise = lookUpKeyOptions(authToken, isAuthenticated)
      const bodyMaterialPromise = lookUpLockMaterials(authToken, isAuthenticated)
      const shackleTypePromise = lookUpShackleTypes(authToken, isAuthenticated)
      const engraveFontOptionsPromise = lookUpEngraveFontOptions(authToken, isAuthenticated)
      const dependenciesPromise = lookUpLockMaterialDependencies(authToken, isAuthenticated)
      const keyCodeBucketTypePromise = lookUpKeyCodeBucketTypes(authToken, isAuthenticated)
      const salesAreasPromise = lookUpSalesAreas(authToken, isAuthenticated)
      const [colors, keyOptions, bodyMaterials, shackleTypes, engraveFontOptions, dependenciesList, keyCodeBucketTypeList, salesAreasList] = await Promise.all([
        colorsPromise,
        keyOptionsPromise,
        bodyMaterialPromise,
        shackleTypePromise,
        engraveFontOptionsPromise,
        dependenciesPromise,
        keyCodeBucketTypePromise,
        salesAreasPromise
      ])
      setLockColorOptions(colors)
      setKeyOptions(keyOptions)
      setLockMaterials(bodyMaterials)
      setShackleTypes(shackleTypes)
      setEngravingFontOptions(engraveFontOptions)
      setLockMaterialDependencies(dependenciesList)
      setKeyCodeBucketTypes(keyCodeBucketTypeList)
      setSalesAreas(salesAreasList)
    }
    catch (e) {
      logger.UI.error('LookupContextProvider: Error Fetching Lookup Options', e)
      toast.error("Error Fetching Lookup Options")
    }

  }
  const getLookups = async (guestAuthenticationKey = '') => {
    if(authState?.isAuthenticated && authState.accessToken?.accessToken){
      logger.UI.log('LookupContextProvider: Fetching Lookup Options')
      let bearerToken = authState.accessToken?.accessToken
      await handleLookUpFetch(bearerToken, true)
    }
    else if (guestAuthenticationKey) {
      logger.UI.log('LookupContextProvider: Fetching Lookup Options as Guest')
      await handleLookUpFetch(guestAuthenticationKey, authState?.isAuthenticated === true)
    }
    else {
      logger.UI.log('LookupContextProvider: No Authentication Token Found')
    }
  }

  useEffect(() => {
    getLookups()
  }, [authState])

  return (
    <LookupContext.Provider
      value={{
        lockColorOptions, setLockColorOptions,
        keyOptions, setKeyOptions,
        lockMaterials, setLockMaterials,
        shackleTypes, setShackleTypes,
        engravingFontOptions, setEngravingFontOptions,
        lockMaterialDependencies, setLockMaterialDependencies,
        keyCodeBucketTypes, setKeyCodeBucketTypes,
        salesAreas, setSalesAreas,
        getLookups
      }}
    >
      {props.children}
    </LookupContext.Provider>
  );
};
